import React, { useState } from 'react';
import { useTeam } from '../../context/TeamContext';
import { useProducts } from '../../context/ProductsContext';
import { useCalculator } from '../../context/CalculatorContext';
import { calculateTeamFTE, generateTeamJustification } from '../../utils/teamCalculations';
import Card from '../Common/Card';
import Button from '../Common/Button';
import Input from '../Common/Input';
import { useToast } from '../Common/Toast';

const ManagerView = () => {
  const { teams, addTeam, modifyTeam, removeTeam } = useTeam();
  const { products } = useProducts();
  const { inputs } = useCalculator();
  const { showToast } = useToast();
  
  const [showCreateTeam, setShowCreateTeam] = useState(false);
  const [teamName, setTeamName] = useState('');
  const [managerName, setManagerName] = useState('');
  const [parentTeamId, setParentTeamId] = useState('');
  const [editingTeamId, setEditingTeamId] = useState(null);
  const [selectedActivities, setSelectedActivities] = useState([]);

  // Debug teams structure
  console.log('teams type:', typeof teams);
  console.log('teams value:', teams);
  console.log('is array?:', Array.isArray(teams));

  // Get all manager teams and director teams with proper type checking
  const managerTeams = Array.isArray(teams) 
    ? teams.filter(t => t.role === 'manager')
    : [];
  const directorTeams = Array.isArray(teams)
    ? teams.filter(t => t.role === 'director') 
    : [];

  const handleCreateTeam = async () => {
    if (!teamName || !managerName) {
      showToast('Please enter team name and manager name', 'error');
      return;
    }

    const result = await addTeam({
      name: teamName,
      managerName: managerName,
      managerEmail: '',
      role: 'manager',
      parentTeamId: parentTeamId || null,
      activities: []
    });

    if (result.success) {
      showToast('Team created successfully!');
      setShowCreateTeam(false);
      setTeamName('');
      setManagerName('');
      setParentTeamId('');
    } else {
      showToast('Failed to create team', 'error');
    }
  };

  const handleToggleActivity = (activityId) => {
    setSelectedActivities(prev => {
      if (prev.includes(activityId)) {
        return prev.filter(id => id !== activityId);
      } else {
        return [...prev, activityId];
      }
    });
  };

  const handleSaveActivities = async (teamId) => {
    if (!teamId) {
      showToast('Invalid team', 'error');
      return;
    }

    // Show loading state immediately
    const loadingToast = showToast('Saving activities...', 'info');
    
    try {
      const result = await modifyTeam(teamId, {
        activities: selectedActivities
      });

      if (result.success) {
        showToast('Activities assigned successfully!');
        setEditingTeamId(null);
        setSelectedActivities([]);
      } else {
        showToast('Failed to assign activities', 'error');
      }
    } catch (error) {
      showToast('Failed to assign activities', 'error');
    }
  };

  const startEditingTeam = (team) => {
    // Use setTimeout to prevent UI blocking
    setTimeout(() => {
      setEditingTeamId(team.id);
      setSelectedActivities(team.activities || []);
    }, 0);
  };

  const handleDeleteTeam = async (teamId, teamName) => {
    if (window.confirm(`Are you sure you want to delete "${teamName}"? This action cannot be undone.`)) {
      const result = await removeTeam(teamId);
      if (result.success) {
        showToast('Team deleted successfully!');
      } else {
        showToast('Failed to delete team', 'error');
      }
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">👤 Manager View</h2>
        <Button onClick={() => setShowCreateTeam(true)} variant="primary">
          + Create New Manager Team
        </Button>
      </div>

      {/* Create Team Modal */}
      {showCreateTeam && (
        <Card title="Create New Manager Team">
          <div className="space-y-4">
            <Input
              label="Team Name"
              value={teamName}
              onChange={(e) => setTeamName(e.target.value)}
              placeholder="e.g., Safety & Compliance Team"
            />
            <Input
              label="Manager Name"
              value={managerName}
              onChange={(e) => setManagerName(e.target.value)}
              placeholder="Your name"
            />
            {directorTeams.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Reports To (Director)
                </label>
                <select
                  value={parentTeamId}
                  onChange={(e) => setParentTeamId(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  aria-label="Select director team to report to"
                >
                  <option value="">-- No Director (Independent) --</option>
                  {directorTeams.map(team => (
                    <option key={team.id} value={team.id}>
                      {team.name} - {team.managerName}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div className="flex gap-3">
              <Button onClick={handleCreateTeam} variant="primary">
                Create Team
              </Button>
              <Button onClick={() => setShowCreateTeam(false)} variant="outline">
                Cancel
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* Manager Teams List */}
      {managerTeams.length > 0 ? (
        <div className="space-y-4">
          {managerTeams.map(team => {
            const teamActivities = products.filter(p => team.activities && team.activities.includes(p.id));
            const teamFTE = calculateTeamFTE(teamActivities, inputs);
            const justification = generateTeamJustification(team, products, teamFTE);
            const isEditing = editingTeamId === team.id;
            const directorTeam = directorTeams.find(d => d.id === team.parentTeamId);

            return (
              <Card key={team.id} title={
                <div className="flex justify-between items-center">
                  <span>{team.name}</span>
                  <button
                    onClick={() => handleDeleteTeam(team.id, team.name)}
                    className="text-red-700 hover:text-red-900 text-sm px-3 py-1 rounded border border-red-400 hover:bg-red-50"
                  >
                    🗑️ Delete Team
                  </button>
                </div>
              }>
                <div className="space-y-4">
                  {/* Team Info */}
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <span className="text-xs text-gray-600">Manager</span>
                        <div className="font-semibold text-gray-900">{team.managerName}</div>
                      </div>
                      <div>
                        <span className="text-xs text-gray-600">Reports To</span>
                        <div className="font-semibold text-gray-900">
                          {directorTeam ? `${directorTeam.name}` : 'Independent'}
                        </div>
                      </div>
                      <div>
                        <span className="text-xs text-gray-600">FTE Required</span>
                        <div className="font-semibold text-green-700 text-xl">{teamFTE.toFixed(2)}</div>
                      </div>
                    </div>
                  </div>

                  {/* Activities */}
                  {!isEditing ? (
                    <>
                      <div>
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-sm font-semibold text-gray-900">📋 Assigned Activities ({teamActivities.length})</span>
                          <Button onClick={() => startEditingTeam(team)} variant="outline" size="sm">
                            Edit Activities
                          </Button>
                        </div>
                        {teamActivities.length > 0 ? (
                          <div className="space-y-2">
                            {teamActivities.map(activity => (
                              <div key={activity.id} className="bg-white border p-2 rounded text-sm">
                                <span className="font-medium">{activity.name}</span>
                                <span className="text-gray-600 ml-2">({activity.baseHours} hrs)</span>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-sm text-gray-600 italic">No activities assigned</p>
                        )}
                      </div>

                      {justification && (
                        <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                          <div className="text-sm font-semibold text-purple-900 mb-2">💼 FTE Justification</div>
                          <p className="text-gray-700 text-sm leading-relaxed">{justification}</p>
                        </div>
                      )}
                    </>
                  ) : (
                    <div>
                      <div className="text-sm font-semibold text-gray-900 mb-2">Select Activities for {team.name}</div>
                      <div className="space-y-2 max-h-96 overflow-y-auto">
                        {products.map(product => {
                          const isSelected = selectedActivities.includes(product.id);
                          return (
                            <div
                              key={product.id}
                              className={`p-3 border-2 rounded-lg cursor-pointer transition-all ${
                                isSelected ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'
                              }`}
                              onClick={() => handleToggleActivity(product.id)}
                            >
                              <div className="flex items-center gap-2">
                                <input type="checkbox" checked={isSelected} onChange={() => {}} className="w-4 h-4" />
                                <div className="flex-1">
                                  <span className="font-semibold text-gray-900 text-sm">{product.name}</span>
                                  <div className="text-xs text-gray-600">
                                    {product.baseHours} hrs | Complexity: {product.comp}% | Risk: {product.risk}%
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      <div className="flex gap-3 mt-4">
                        <Button onClick={() => handleSaveActivities(team.id)} variant="primary">
                          Save ({selectedActivities.length} selected)
                        </Button>
                        <Button onClick={() => { setEditingTeamId(null); setSelectedActivities([]); }} variant="outline">
                          Cancel
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      ) : (
        !showCreateTeam && (
          <Card>
            <div className="text-center py-12">
              <div className="text-6xl mb-4">👥</div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">No Manager Teams Yet</h3>
              <p className="text-gray-600 mb-4">Create a team to start assigning activities and calculating FTE requirements.</p>
              <Button onClick={() => setShowCreateTeam(true)} variant="primary">
                Create Your First Team
              </Button>
            </div>
          </Card>
        )
      )}
    </div>
  );
};

export default ManagerView;
